import Storage from "@/utils/storage";
import { Platform } from "@xt/xtbase";
import { REGISTER_PENDING_COUPON_STORAGE_KEY } from "@/utils/registerPendingCouponGrants";
import {
  hasUnusedCouponsFromUserInfo,
  shouldShowUnusedCouponHomePrompt,
} from "@/utils/unusedCouponHomePrompt";

const COOLDOWN_MS = 24 * 60 * 60 * 1000;

const SKIP_PATH_PREFIXES = ["/accounts/login", "/accounts/register", "/accounts/user/mobile"];

function readPromptRecord() {
  if (typeof window === "undefined") return null;
  const raw = Storage.get("bindPhonePrompt");
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw;
}

function writePromptRecord(userId, patch) {
  if (userId == null || userId === "") return;
  const uid = String(userId);
  const prev = readPromptRecord();
  const base = prev?.userId === uid ? prev : { userId: uid };
  Storage.set("bindPhonePrompt", {
    userId: uid,
    lastShownAt: patch.lastShownAt !== undefined ? patch.lastShownAt : base.lastShownAt,
  });
}

function getCurrentUserRecord(userId) {
  if (userId == null || userId === "") return {};
  const row = readPromptRecord();
  if (!row || String(row.userId) !== String(userId)) return {};
  return row;
}

/** mobileAuth：是否手机认证（绑定手机号）。getUserInfo.data.mobileAuth，0 未绑定，1 已绑定 */
export function isUserMobileBound(userInfo) {
  const auth = userInfo?.mobileAuth;
  return auth === true || auth === 1 || auth === "1";
}

function isBindMobileCouponGranted(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function formatCouponAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 8 }).format(amount);
}

/**
 * 绑定手机赠券：getUserInfo.data.bindMobileCoupon。
 * alreadyGranted 为 true 时不再展示赠券金额。
 */
export function getBindMobileCouponOffer(userInfo) {
  const coupon = userInfo?.bindMobileCoupon ?? userInfo?.bind_mobile_coupon;
  if (!coupon || typeof coupon !== "object") return null;
  if (isBindMobileCouponGranted(coupon.alreadyGranted ?? coupon.already_granted)) return null;
  const amount = formatCouponAmount(coupon.benefitValue ?? coupon.benefit_value);
  if (!amount) return null;
  const name = String(coupon.displayName ?? coupon.display_name ?? "").trim();
  return { amount, name };
}

export function shouldSkipBindPhonePromptPath(pathname) {
  if (!pathname) return false;
  return SKIP_PATH_PREFIXES.some((prefix) => pathname.includes(prefix));
}

/** App 内 WebView 不展示绑定手机 H5 弹窗（由原生侧处理或无需打扰） */
export function shouldSkipBindPhonePromptInApp() {
  if (typeof window === "undefined") return false;
  return Platform.isInApp();
}

function hasRegisterPendingCouponGrants() {
  if (typeof window === "undefined") return false;
  const raw = localStorage.getItem(REGISTER_PENDING_COUPON_STORAGE_KEY);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return false;
    const grants = parsed.grants ?? parsed.registerCouponGrants ?? parsed;
    return grants != null;
  } catch {
    return false;
  }
}

/** 首页注册送券 / 未使用券每日弹窗优先，避免叠弹 */
export function shouldDeferBindPhoneForHomeCouponPrompt(userId, userInfo, pathname) {
  if (pathname !== "/") return false;
  if (hasRegisterPendingCouponGrants()) return true;
  if (!userId) return false;
  if (!hasUnusedCouponsFromUserInfo(userInfo)) return false;
  return shouldShowUnusedCouponHomePrompt(userId);
}

export function shouldShowBindPhonePrompt(userId) {
  if (userId == null || userId === "") return false;
  const lastShownAt = Number(getCurrentUserRecord(userId).lastShownAt);
  if (!Number.isFinite(lastShownAt)) return true;
  return Date.now() - lastShownAt >= COOLDOWN_MS;
}

export function markBindPhonePromptShown(userId) {
  if (userId == null || userId === "") return;
  writePromptRecord(userId, { lastShownAt: Date.now() });
}
